import React, { useState, useEffect } from "react";
import clsx from "clsx";

import { useAppSelector } from "@/store/hooks";

import { tooltipTitles } from "@/constants/tooltipTitles";
import { appDate } from "@/utils/date";

import { Lead } from "@/types";

import { useAssemblyAI } from "@/hooks/useAssemblyAI";
import { getPresignedUrl } from "@/utils/aws";

import AppCard from "@/components/AppCard";
import AppBox from "@/components/AppBox";
import AppText from "@/components/AppText";
import AppInput from "@/components/AppInput";
import AppIcon from "@/components/AppIcon";
import AppTooltip from "@/components/AppTooltip";
import AppInfoSection from "@/components/AppInfoSection";
import AppButton from "@/components/AppButton";
import AppSwitch from "@/components/AppSwitch";
import SummaryList from "./SummaryList";

interface LeadInfoCardProps {
  lead: Lead;
  onAiCallChange?: (data: any) => void;
  onChange?: (data: any) => void;
}

const LeadInfoCard: React.FC<LeadInfoCardProps> = ({ lead, onAiCallChange, onChange }) => {
  const client = useAssemblyAI()

  const { isMobile } = useAppSelector((state) => state.app);
  const { me } = useAppSelector((state) => state.auth);

  const [leadForm, setLeadForm] = useState({
    name: lead?.name || `LSA ${lead?.type} Lead`,
    phone: lead?.phone || "empty",
  });
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  useEffect(() => {
    if (lead?.id) {
      setLeadForm({
        name: lead.name || `LSA ${lead?.type} Lead` as string,
        phone: lead.phone || "empty",
      });
    }
  }, [lead]);

  const fetchFile = async (key: string) => {
    try {
      const url = await getPresignedUrl(`${key}.mp3`);
      return url
    } catch (error) {
      console.error("Error fetching file", error);
      return null;
    }
  };

  const handleProcessCall = async () => {
    setLoadingTranscript(true);
    try {
      const fileUrl = await fetchFile(lead.aiCall.audio_key);
      if (fileUrl) {
        const config = { audio_url: fileUrl };
        const transcript = await client.transcripts.transcribe(config);
        
        if (transcript && onAiCallChange) {
          const { response } = await client.lemur.task({
            transcript_ids: [transcript.id.toString()],
            prompt: 'Provide a brief summary of the transcript.',
            final_model: 'anthropic/claude-3-5-sonnet'
          });

          onAiCallChange({
            transcript: transcript.text,
            transcript_id: transcript.id,
            summary: response
          });
        }
      }
    } catch (error) {
      console.error("Error processing call:", error);
    }
    setLoadingTranscript(false);
  };

  const handleChangeLeadName = (value: any) => {
    setLeadForm(prevState => ({
      ...prevState,
      name: value,
    }));
  }

  const handleToggleEdit = () => {
    setIsEdit((prev) => !prev);
  }

  const handleSaveLeadName = () => {
    if (onChange) {
      onChange({name: leadForm.name})
    }
    handleToggleEdit()
  }

  const handleCancelEdit = () => {
    setLeadForm(prevState => ({
      ...prevState,
      name: lead.name || `LSA ${lead?.type} Lead`
    }))
    handleToggleEdit()
  }

  const handleUpdateLead = () => {
    if (onChange) {
      onChange({pause_followups: !lead.pause_followups})
    }
  }

  const infoList = [
    {
      label: "Pro's Company Name",
      // labelTooltip: tooltipTitles.leadPhone,
      content: (
        <AppText>
          {me.company}
        </AppText>
      ),
    },
    {
      label: "Name",
      labelTooltip: tooltipTitles.leadName,
      content: (
        <AppInput
          value={leadForm.name}
          onChange={(value) => handleChangeLeadName(value)}
          placeholder="e.g. John Doe"
          inputClassName={clsx(
            "text-center md:text-left text-base",
            {
              "text-gray-700": !isEdit,
              "text-gray-900": isEdit,
            }
          )}
          className={clsx(
            "group transition-all duration-300 border -ml-3",
            {
              "border-transparent focus:ring-0 bg-transparent text-gray-700": !isEdit,
              "border focus:ring-primary bg-white text-gray-900": isEdit,
            }
          )}
          disabled={!isEdit}
          endAdornment={
            isEdit
              ? <AppBox gap={1}>
                  <AppTooltip title="Cancel Editing">
                    <AppIcon
                      name="x"
                      className="text-error cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevents input focus
                        handleCancelEdit();
                      }}
                    />
                  </AppTooltip>

                  <AppTooltip title="Save Lead name">
                    <AppIcon
                      name="check"
                      className="text-primary cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevents input focus
                        handleSaveLeadName();
                      }}
                    />
                  </AppTooltip>
                </AppBox>
              : <AppTooltip title="Edit Lead Name">
                  <AppIcon
                    name="edit"
                    className={clsx(
                      "opacity-0 cursor-pointer",
                      {
                        "group-hover:opacity-100": !isEdit,
                        "opacity-100": isMobile
                      }
                    )}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevents input focus
                      handleToggleEdit();
                    }}
                  />
                </AppTooltip>
          }
        />
      ),
    },
    {
      label: "Phone",
      labelTooltip: tooltipTitles.leadPhone,
      content: (
        <AppText variant={leadForm.phone === "empty" ? "caption" : "body1"}>
          {leadForm.phone}
        </AppText>
      ),
    },
    {
      label: "Date",
      labelTooltip: tooltipTitles.leadDate,
      content: (
        <AppText variant="body1">
          { appDate(lead?.created_at) }
        </AppText>
      ),
    },
    {
      label: "Pause Follow-Ups",
      labelTooltip: tooltipTitles.pauseFollowups,
      content: (
        <AppText variant="body1">
          <AppSwitch
            checked={lead?.pause_followups}
            onChange={handleUpdateLead}
          />
        </AppText>
      ),
    },
    {
      label: "Type",
      labelTooltip: tooltipTitles.leadType,
      content: <AppText variant="body1">
        {/* {lead?.type === "Call" ? "Answered Call" : lead?.type} */}
        {lead?.type}
      </AppText>,
    },
    {
      label: "Service Type",
      labelTooltip: tooltipTitles.leadServiceType,
      content: (
        <AppText variant={!lead?.service ? "caption" : "body1"}>
          {lead?.service || "empty"}
        </AppText>
      ),
    },
    {
      label: "Location",
      labelTooltip: tooltipTitles.leadLocation,
      content: (
        <AppText variant={!lead?.location ? "caption" : "body1"}>
          {lead?.location || "empty"}
        </AppText>
      ),
    },
    {
      label: "Call Result",
      labelTooltip: tooltipTitles.aiCallResult,
      content: lead?.aiCall?.audio_key && lead?.aiCall?.bucket
        ? <AppBox column gap={4}>
            {
              lead?.aiCall?.bucket
                ? <AppText variant="body1">{lead.aiCall.bucket}</AppText>
                : <AppButton className="w-[160px]" onClick={handleProcessCall} isLoading={loadingTranscript}>Process Call</AppButton>
            }
          </AppBox>
        : null,
    },
    {
      label: "Call Summary",
      labelTooltip: tooltipTitles.aiCallSummary,
      content: lead?.aiCall?.audio_key && lead?.aiCall?.summary
        ? <AppBox column gap={4}>
            {
              lead?.aiCall?.summary
                ? <SummaryList summary={lead.aiCall.summary} />
                : <AppButton className="w-[160px]" onClick={handleProcessCall} isLoading={loadingTranscript}>Process Call</AppButton>
            }
          </AppBox>
        : null,
    },
  ];

  return (
    <AppCard className="overflow-auto">
      <dl className="divide-y divide-gray-100">
        {infoList.map((item, index) => (
          item.content
            ? <AppInfoSection key={index} label={item.label} labelTootltip={item.labelTooltip} content={item.content} className={item.label === "Call Summary" ? "items-start" : "items-center"} />
            : null
        ))}
      </dl>
    </AppCard>
  );
};

export default LeadInfoCard;